<script setup lang="ts">
import { useElementBounding } from '@vueuse/core/index.cjs';
import { type FragmentType, graphql } from '~/gql';

const props = defineProps<{ speaker: FragmentType<typeof SpeakerFragment> }>();

const SpeakerFragment = graphql(`
  fragment SeminarDetailTemplateSpeakerInformationSpeaker on Speaker {
    id
    iconPath
    name
    description
    planner {
      id
    }
  }
`);

const speaker = useComputedFragment(SpeakerFragment, () => props.speaker);
const { buildImgixURL } = useImgixURL();
const avaterUrl = computed(() =>
  buildImgixURL(speaker.value.iconPath, {
    faceFit: true,
    width: 160,
    height: 160,
    quality: 100,
  })
);
const profileUrl = computed(() => {
  if (!speaker.value.planner) return null;
  return `/planner/${speaker.value.planner.id}`;
});

const expanded = ref(false);

// body を入れる箱の content height が body の高さよりもおおきかったら expanded にする
const boxPaddingBottom = 16;
const bodyBox = ref<HTMLElement>();
const { height: boxHeight } = useElementBounding(bodyBox);
const body = ref<HTMLElement>();
const { height: textHeight } = useElementBounding(body);
watch(
  [boxHeight, textHeight],
  ([b, t]) => {
    if (expanded.value) return;
    if (b === 0 || t === 0) return;
    expanded.value = b - boxPaddingBottom >= t;
  },
  { immediate: true }
);
</script>

<template>
  <div :class="$style.container">
    <img :class="$style.container__avater" :src="avaterUrl" />
    <div :class="$style.container__name">{{ speaker.name }}</div>
    <div :class="[$style.container__description, $style.description]">
      <div
        ref="bodyBox"
        :class="[
          $style.description__body,
          { [$style['description__body--hidden']]: !expanded },
        ]"
      >
        <span ref="body">{{ speaker.description }}</span>
      </div>
      <ClientOnly>
        <div
          v-if="!expanded"
          :class="$style.description__button"
          @click="expanded = true"
        >
          もっと見る
        </div>
      </ClientOnly>
    </div>
    <NuxtLink
      v-if="profileUrl"
      :class="$style.container__link"
      :href="profileUrl"
      >この講師のプロフィールをみる</NuxtLink
    >
  </div>
</template>

<style module lang="scss">
.container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;

  &__avater {
    width: 80px;
    border-radius: 100vw;
  }

  &__name {
    font-size: 16px;
    font-weight: bold;
    line-height: 1.8;
  }

  &__description {
    margin: 12px 0;
  }

  &__link {
    font-size: 16px;
    align-self: flex-end;
    text-decoration: underline;
  }
}

.description {
  position: relative;
  &__body {
    font-size: 16px;
    line-height: 1.8;
    &--hidden {
      padding-bottom: v-bind('`${boxPaddingBottom}px`');
      display: -webkit-box;
      -webkit-line-clamp: 5;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;

      mask-image: linear-gradient(to top, transparent 16px, white 35%);
    }
  }

  &__button {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);

    background-color: $color-surface-primary;
    padding: 8px 24px;
    border-radius: 100vw;
    border: 1px solid $color-border-low-emphasis;
    font-weight: bold;

    &:hover {
      cursor: pointer;
    }
  }
}
</style>
